<template>
  <div class="api-logs-view">
    <!-- Notification -->
    <transition name="fade">
      <div v-if="notification.show" 
           class="notification" 
           :class="notification.type">
        <div class="notification-content">
          <svg v-if="notification.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
          <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
          <span>{{ notification.message }}</span>
        </div>
      </div>
    </transition>

    <PageHeader 
      pageName="Logs API" 
      :pageTitle="`Logs API - ${accountInfo.display_phone_number || 'Chargement...'}`" 
      pageSubtitle="Consultez l'historique des requêtes API et des webhooks pour ce compte WhatsApp."
    >
      <template #actions>
        <button @click="refreshLogs" class="sleek-add-btn" :disabled="loading" style="background: #1EA355;">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16"/>
          </svg>
          Actualiser
        </button>
      </template>
    </PageHeader>

    <!-- Stats Cards -->
    <div v-if="stats" class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon success">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.total.successCount || 0 }}</div>
          <div class="stat-label">Succès</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon error">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.total.errorCount || 0 }}</div>
          <div class="stat-label">Erreurs</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon total">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 3h18v18H3zM3 9h18M9 21V9"/>
          </svg>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.total.totalRequests || 0 }}</div>
          <div class="stat-label">Total</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon time">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 6v6l4 2"/>
          </svg>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ formatResponseTime(stats.total.avgResponseTime) }}</div>
          <div class="stat-label">Temps moyen</div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-section">
      <div class="filters-row">
        <div class="filter-group">
          <label>Direction</label>
          <select v-model="filters.direction" @change="applyFilters">
            <option value="">Toutes</option>
            <option value="outgoing">Sortantes</option>
            <option value="incoming">Entrantes</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Statut</label>
          <select v-model="filters.status" @change="applyFilters">
            <option value="">Tous</option>
            <option value="success">Succès</option>
            <option value="error">Erreur</option>
            <option value="timeout">Timeout</option>
            <option value="pending">En attente</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Type</label>
          <select v-model="filters.type" @change="applyFilters">
            <option value="">Tous</option>
            <option value="webhook_forward">Webhook Forward</option>
            <option value="api_call">API Call</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Date début</label>
          <input type="date" v-model="filters.dateFrom" @change="applyFilters">
        </div>

        <div class="filter-group">
          <label>Date fin</label>
          <input type="date" v-model="filters.dateTo" @change="applyFilters">
        </div>

        <button @click="resetFilters" class="reset-filters-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
          Réinitialiser
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10" stroke-dasharray="60" stroke-dashoffset="60">
            <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
          </circle>
        </svg>
      </div>
      <p>Chargement des logs...</p>
    </div>

    <!-- Logs List -->
    <div v-else-if="logs.length > 0" class="logs-container">
      <div class="logs-list">
        <div v-for="log in logs" :key="log._id" class="log-card" :class="log.direction">
          <div class="log-header" @click="toggleLogExpansion(log._id)">
            <div class="log-main-info">
              <div class="log-direction-icon" :class="log.direction">
                <svg v-if="log.direction === 'outgoing'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M7 17L17 7M17 7H7M17 7v10"/>
                </svg>
                <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M17 7L7 17M7 17h10M7 17V7"/>
                </svg>
              </div>
              
              <div class="log-summary">
                <div class="log-title">
                  <span class="type-badge" :class="log.direction">
                    {{ log.direction === 'outgoing' ? '🚀 SORTANTE' : '📥 ENTRANTE' }}
                  </span>
                  <span v-if="isBusinessStatusUpdate(log) && getBusinessStatusInfo(log)" class="business-status-badge">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    {{ getBusinessStatusInfo(log).status.toUpperCase() }}
                  </span>
                  <span class="status-badge" :class="log.status">
                    {{ getStatusLabel(log.status) }}
                  </span>
                  <span class="method-badge">{{ log.request?.method || 'N/A' }}</span>
                  <span v-if="log.retry_count > 0" class="retry-badge">
                    Retry {{ log.retry_count }}
                  </span>
                </div>
                
                <div class="log-essential-info">
                  <span v-if="log.whatsapp_message?.from" class="info-chip phone">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    {{ log.whatsapp_message.from }}
                  </span>
                  <span v-if="log.whatsapp_message?.message_type" class="info-chip message-type">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                    {{ log.whatsapp_message.message_type }}
                  </span>
                  <span class="info-chip">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 6v6l4 2"/>
                    </svg>
                    {{ formatResponseTime(log.response?.response_time) }}
                  </span>
                  <span class="info-chip" :class="getStatusClass(log.response?.status_code)">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"/>
                    </svg>
                    {{ log.response?.status_code || 'N/A' }}
                  </span>
                  <span class="info-chip time">
                    {{ formatDate(log.created_at) }}
                  </span>
                  <!-- Destinataire pour les statuts Business -->
                  <span v-if="isBusinessStatusUpdate(log) && getBusinessStatusInfo(log)" class="info-chip recipient">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="8.5" cy="7" r="4"/>
                      <path d="M20 8v6M23 11h-6"/>
                    </svg>
                    {{ getBusinessStatusInfo(log).recipient }}
                  </span>
                </div>
                
                <!-- Message Preview -->
                <div v-if="!isBusinessStatusUpdate(log) && getMessagePreview(log)" class="message-preview">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  <span>{{ getMessagePreview(log) }}</span>
                </div>
              </div>
            </div>

            <button class="expand-btn" :class="{ expanded: expandedLogs.includes(log._id) }">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </button>
          </div>

          <!-- Expanded Details -->
          <transition name="expand">
            <div v-if="expandedLogs.includes(log._id)" class="log-body">
              <div class="log-details-grid">
                <div class="detail-row">
                  <span class="detail-label">Type</span>
                  <span class="detail-value">{{ log.type === 'webhook_forward' ? 'Webhook Forward' : 'API Call' }}</span>
                </div>
                
                <div v-if="log.request?.url" class="detail-row full">
                  <span class="detail-label">URL</span>
                  <span class="detail-value url">{{ log.request.url }}</span>
                </div>

                <div v-if="log.whatsapp_message" class="detail-row full whatsapp-message">
                  <div class="message-header">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                    Message WhatsApp
                  </div>
                  <div class="message-details">
                    <span>De: <strong>{{ log.whatsapp_message.from }}</strong></span>
                    <span>À: <strong>{{ log.whatsapp_message.to }}</strong></span>
                    <span>Type: <strong>{{ log.whatsapp_message.message_type }}</strong></span>
                  </div>
                </div>

                <div v-if="log.error_message" class="detail-row full error-message">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 8v4M12 16h.01"/>
                  </svg>
                  {{ log.error_message }}
                </div>
              </div>

              <div class="log-actions">
                <button class="action-btn primary" @click.stop="openLogDetail(log)">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  Voir détails complets
                </button>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.totalPages > 1" class="pagination">
        <button @click="changePage(pagination.page - 1)" :disabled="pagination.page === 1" class="page-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
          Précédent
        </button>
        
        <div class="page-numbers">
          <button 
            v-for="page in getPageNumbers()" 
            :key="page"
            @click="changePage(page)"
            :class="['page-number', { active: page === pagination.page }]"
            :disabled="page === '...'"
          >
            {{ page }}
          </button>
        </div>

        <button @click="changePage(pagination.page + 1)" :disabled="pagination.page === pagination.totalPages" class="page-btn">
          Suivant
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2"/>
      </svg>
      <h3>Aucun log disponible</h3>
      <p>Il n'y a pas encore de logs d'API pour ce compte WhatsApp.</p>
    </div>

    <!-- Log Detail Modal -->
    <div v-if="showDetailModal" class="modal-overlay" @click.self="closeDetailModal">
      <div class="modal-content log-detail-modal">
        <div class="modal-header">
          <h3>Détails du Log</h3>
          <button class="close-btn" @click="closeDetailModal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div class="modal-body" v-if="selectedLog">
          <!-- Summary Cards -->
          <div class="summary-cards">
            <div class="summary-card" :class="selectedLog.direction">
              <div class="summary-card-icon">
                <svg v-if="selectedLog.direction === 'outgoing'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M7 17L17 7M17 7H7M17 7v10"/>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M17 7L7 17M7 17h10M7 17V7"/>
                </svg>
              </div>
              <div class="summary-card-content">
                <div class="summary-card-label">Direction</div>
                <div class="summary-card-value">{{ selectedLog.direction === 'outgoing' ? '🚀 Sortante' : '📥 Entrante' }}</div>
              </div>
            </div>

            <div class="summary-card" :class="'status-' + selectedLog.status">
              <div class="summary-card-icon">
                <svg v-if="selectedLog.status === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 8v4M12 16h.01"/>
                </svg>
              </div>
              <div class="summary-card-content">
                <div class="summary-card-label">Statut</div>
                <div class="summary-card-value">{{ getStatusLabel(selectedLog.status) }}</div>
              </div>
            </div>

            <div class="summary-card">
              <div class="summary-card-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 6v6l4 2"/>
                </svg>
              </div>
              <div class="summary-card-content">
                <div class="summary-card-label">Temps de réponse</div>
                <div class="summary-card-value">{{ formatResponseTime(selectedLog.response?.response_time) }}</div>
              </div>
            </div>

            <div class="summary-card">
              <div class="summary-card-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 2v20M2 12h20"/>
                </svg>
              </div>
              <div class="summary-card-content">
                <div class="summary-card-label">Méthode</div>
                <div class="summary-card-value">{{ selectedLog.request?.method || 'N/A' }}</div>
              </div>
            </div>
          </div>

          <!-- Request Section -->
          <div class="detail-section-modern">
            <div class="section-header-modern">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              <h4>Requête</h4>
            </div>

            <div class="info-list">
              <div class="info-list-item">
                <span class="info-list-label">URL</span>
                <span class="info-list-value url-value">{{ selectedLog.request?.url }}</span>
              </div>
              <div class="info-list-item">
                <span class="info-list-label">Taille</span>
                <span class="info-list-value">{{ formatBytes(selectedLog.request?.body_size) }}</span>
              </div>
              <div class="info-list-item">
                <span class="info-list-label">Envoyé à</span>
                <span class="info-list-value">{{ formatTime(selectedLog.request?.sent_at) }}</span>
              </div>
            </div>

            <!-- Request Headers -->
            <div v-if="selectedLog.request?.headers" class="code-section-modern">
              <div class="code-header-modern">
                <span>Headers</span>
                <button @click="copyToClipboard(selectedLog.request.headers)" class="copy-btn-modern">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                  Copier
                </button>
              </div>
              <pre class="code-block-modern">{{ JSON.stringify(selectedLog.request.headers, null, 2) }}</pre>
            </div>

            <!-- Request Body -->
            <div v-if="selectedLog.request?.body" class="code-section-modern">
              <div class="code-header-modern">
                <span>Body</span>
                <button @click="copyToClipboard(selectedLog.request.body)" class="copy-btn-modern">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                  Copier
                </button>
              </div>
              <pre class="code-block-modern">{{ JSON.stringify(selectedLog.request.body, null, 2) }}</pre>
            </div>
          </div>

          <!-- Response Section -->
          <div v-if="selectedLog.response" class="detail-section-modern">
            <div class="section-header-modern">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
              </svg>
              <h4>Réponse</h4>
            </div>

            <div class="info-list">
              <div class="info-list-item">
                <span class="info-list-label">Status Code</span>
                <span class="status-code-badge-modern" :class="getStatusClass(selectedLog.response.status_code)">
                  {{ selectedLog.response.status_code }} {{ selectedLog.response.status_text }}
                </span>
              </div>
              <div class="info-list-item">
                <span class="info-list-label">Taille</span>
                <span class="info-list-value">{{ formatBytes(selectedLog.response.body_size) }}</span>
              </div>
              <div class="info-list-item">
                <span class="info-list-label">Reçu à</span>
                <span class="info-list-value">{{ formatTime(selectedLog.response.received_at) }}</span>
              </div>
            </div>

            <!-- Response Body -->
            <div v-if="selectedLog.response.body" class="code-section-modern">
              <div class="code-header-modern">
                <span>Body</span>
                <button @click="copyToClipboard(selectedLog.response.body)" class="copy-btn-modern">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                  Copier
                </button>
              </div>
              <pre class="code-block-modern">{{ JSON.stringify(selectedLog.response.body, null, 2) }}</pre>
            </div>
          </div>

          <!-- WhatsApp Message Info -->
          <div v-if="selectedLog.whatsapp_message" class="detail-section-modern whatsapp-section">
            <div class="section-header-modern">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              <h4>Message WhatsApp</h4>
            </div>

            <div class="info-list">
              <div class="info-list-item">
                <span class="info-list-label">Message ID</span>
                <span class="info-list-value monospace-value">{{ selectedLog.whatsapp_message.message_id }}</span>
              </div>
              <div class="info-list-item">
                <span class="info-list-label">De</span>
                <span class="info-list-value">{{ selectedLog.whatsapp_message.from }}</span>
              </div>
              <div class="info-list-item">
                <span class="info-list-label">À</span>
                <span class="info-list-value">{{ selectedLog.whatsapp_message.to }}</span>
              </div>
              <div class="info-list-item">
                <span class="info-list-label">Type</span>
                <span class="info-list-value">{{ selectedLog.whatsapp_message.message_type }}</span>
              </div>
            </div>
          </div>

          <!-- Error Section -->
          <div v-if="selectedLog.error || selectedLog.error_message" class="detail-section-modern error-section-modern">
            <div class="section-header-modern error-header">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 8v4M12 16h.01"/>
              </svg>
              <h4>Erreur</h4>
            </div>

            <div class="error-content-modern">
              <div v-if="selectedLog.error" class="error-item-modern">
                <strong>Code:</strong> {{ selectedLog.error }}
              </div>
              <div v-if="selectedLog.error_message" class="error-item-modern">
                <strong>Message:</strong> {{ selectedLog.error_message }}
              </div>
            </div>
          </div>

          <!-- Retry Info -->
          <div v-if="selectedLog.retry_count > 0 || selectedLog.is_retry" class="detail-section-modern">
            <div class="section-header-modern">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              <h4>Informations de Retry</h4>
            </div>

            <div class="info-list">
              <div class="info-list-item">
                <span class="info-list-label">Nombre de retry</span>
                <span class="info-list-value">{{ selectedLog.retry_count }}</span>
              </div>
              <div class="info-list-item">
                <span class="info-list-label">Est un retry</span>
                <span class="info-list-value">{{ selectedLog.is_retry ? 'Oui' : 'Non' }}</span>
              </div>
              <div v-if="selectedLog.original_log_id" class="info-list-item">
                <span class="info-list-label">Log original</span>
                <span class="info-list-value monospace-value">{{ selectedLog.original_log_id }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { axiosPeelo } from '@/utils/axios';
import { consoleError } from '@/utils/logger';

export default {
  name: 'ApiLogsView',
  components: {
    PageHeader: () => import('@/components/PageHeader.vue')
  },
  data() {
    return {
      loading: true,
      logs: [],
      stats: null,
      accountInfo: {},
      filters: {
        direction: '',
        status: '',
        type: '',
        dateFrom: '',
        dateTo: '',
        phone_number: ''
      },
      pagination: {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0
      },
      notification: {
        show: false,
        message: '',
        type: 'success',
        timeout: null
      },
      showDetailModal: false,
      selectedLog: null,
      expandedLogs: []
    };
  },
  computed: {
    whatsappAccountId() {
      return this.$route.params.idwhatsapp;
    }
  },
  methods: {
    async fetchLogs() {
      this.loading = true;
      try {
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Données utilisateur non trouvées');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;

        const params = {
          whatsapp_account_id: this.whatsappAccountId,
          page: this.pagination.page,
          limit: this.pagination.limit,
          ...this.filters
        };

        // Nettoyer les paramètres vides
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key];
          }
        });

        const response = await axiosPeelo.get('/get/strong/api-logs', {
          params,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data && response.data.success) {
          this.logs = response.data.data.logs || [];
          this.pagination = {
            ...this.pagination,
            ...response.data.data.pagination
          };
        }
      } catch (err) {
        consoleError('Error fetching logs:', err);
        this.showNotification(
          err.response?.data?.error || 'Erreur lors du chargement des logs',
          'error'
        );
      } finally {
        this.loading = false;
      }
    },

    async fetchStats() {
      try {
        const userData = localStorage.getItem('user');
        if (!userData) return;
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;

        const params = {};
        if (this.filters.dateFrom) params.dateFrom = this.filters.dateFrom;
        if (this.filters.dateTo) params.dateTo = this.filters.dateTo;

        const response = await axiosPeelo.get(
          `/get/strong/api-logs/stats/${this.whatsappAccountId}`,
          {
            params,
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.data && response.data.success) {
          this.stats = response.data.data.stats;
        }
      } catch (err) {
        consoleError('Error fetching stats:', err);
      }
    },

    async fetchAccountInfo() {
      try {
        const userData = localStorage.getItem('user');
        if (!userData) return;
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;

        const response = await axiosPeelo.get('/strong/whatsapp/accounts', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data) {
          const accounts = response.data;
          const account = accounts.find(acc => acc._id === this.whatsappAccountId);
          if (account) {
            this.accountInfo = account;
          }
        }
      } catch (err) {
        consoleError('Error fetching account info:', err);
      }
    },

    applyFilters() {
      this.pagination.page = 1;
      this.fetchLogs();
      this.fetchStats();
    },

    resetFilters() {
      this.filters = {
        direction: '',
        status: '',
        type: '',
        dateFrom: '',
        dateTo: '',
        phone_number: ''
      };
      this.applyFilters();
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages) return;
      this.pagination.page = page;
      this.fetchLogs();
    },

    getPageNumbers() {
      const pages = [];
      const current = this.pagination.page;
      const total = this.pagination.totalPages;

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        if (current <= 3) {
          pages.push(1, 2, 3, 4, '...', total);
        } else if (current >= total - 2) {
          pages.push(1, '...', total - 3, total - 2, total - 1, total);
        } else {
          pages.push(1, '...', current - 1, current, current + 1, '...', total);
        }
      }

      return pages;
    },

    refreshLogs() {
      this.fetchLogs();
      this.fetchStats();
      this.showNotification('Logs actualisés', 'success');
    },

    toggleLogExpansion(logId) {
      const index = this.expandedLogs.indexOf(logId);
      if (index > -1) {
        this.expandedLogs.splice(index, 1);
      } else {
        this.expandedLogs.push(logId);
      }
    },

    openLogDetail(log) {
      this.selectedLog = log;
      this.showDetailModal = true;
    },

    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedLog = null;
    },

    goBack() {
      this.$router.push('/dashboard/whatsapp-signup');
    },

    getStatusLabel(status) {
      const labels = {
        success: 'Succès',
        error: 'Erreur',
        timeout: 'Timeout',
        pending: 'En attente'
      };
      return labels[status] || status;
    },

    getStatusClass(statusCode) {
      if (!statusCode) return '';
      if (statusCode >= 200 && statusCode < 300) return 'success';
      if (statusCode >= 400 && statusCode < 500) return 'client-error';
      if (statusCode >= 500) return 'server-error';
      return '';
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },

    formatTime(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    formatResponseTime(ms) {
      if (!ms && ms !== 0) return 'N/A';
      if (ms < 1000) return `${Math.round(ms)}ms`;
      const seconds = ms / 1000;
      if (seconds < 10) return `${seconds.toFixed(1)}s`;
      return `${Math.round(seconds)}s`;
    },

    formatBytes(bytes) {
      if (!bytes && bytes !== 0) return 'N/A';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    },

    truncateUrl(url) {
      if (!url) return 'N/A';
      if (url.length <= 60) return url;
      return url.substring(0, 57) + '...';
    },

    isBusinessStatusUpdate(log) {
      // Détecter si c'est un statut de message envoyé depuis WhatsApp Business
      try {
        if (log.request?.body) {
          const body = typeof log.request.body === 'string' 
            ? JSON.parse(log.request.body) 
            : log.request.body;
          
          // Format direct : { changes: [{ value: { statuses: [...] } }] }
          if (body.changes?.[0]?.value?.statuses) {
            return true;
          }
          
          // Format avec entry : { entry: [{ changes: [{ value: { statuses: [...] } }] }] }
          if (body.entry?.[0]?.changes?.[0]?.value?.statuses) {
            return true;
          }
        }
      } catch (error) {
        consoleError('Error in isBusinessStatusUpdate:', error);
        return false;
      }
      return false;
    },

    getBusinessStatusInfo(log) {
      // Extraire les infos de statut pour affichage
      try {
        if (log.request?.body) {
          const body = typeof log.request.body === 'string' 
            ? JSON.parse(log.request.body) 
            : log.request.body;
          
          // Format direct
          if (body.changes?.[0]?.value?.statuses?.[0]) {
            const status = body.changes[0].value.statuses[0];
            return {
              status: status.status,
              recipient: status.recipient_id,
              timestamp: status.timestamp
            };
          }
          
          // Format avec entry
          if (body.entry?.[0]?.changes?.[0]?.value?.statuses?.[0]) {
            const status = body.entry[0].changes[0].value.statuses[0];
            return {
              status: status.status,
              recipient: status.recipient_id,
              timestamp: status.timestamp
            };
          }
        }
      } catch (error) {
        consoleError('Error in getBusinessStatusInfo:', error);
        return null;
      }
      return null;
    },

    getMessagePreview(log) {
      try {
        // Essayer d'extraire le contenu du message depuis le body de la requête
        if (log.request?.body) {
          const body = typeof log.request.body === 'string' 
            ? JSON.parse(log.request.body) 
            : log.request.body;
          
          // Chercher dans différentes structures possibles
          let text = '';
          
          // Structure WhatsApp standard
          if (body.entry?.[0]?.changes?.[0]?.value?.messages?.[0]) {
            const message = body.entry[0].changes[0].value.messages[0];
            
            if (message.text?.body) {
              text = message.text.body;
            } else if (message.image?.caption) {
              text = `📷 ${message.image.caption}`;
            } else if (message.image) {
              text = '📷 Image';
            } else if (message.document) {
              text = '📄 Document';
            } else if (message.audio) {
              text = '🎵 Audio';
            } else if (message.video) {
              text = '🎬 Vidéo';
            } else if (message.location) {
              text = '📍 Localisation';
            } else if (message.contacts) {
              text = '👤 Contact';
            }
          }
          
          // Tronquer si trop long
          if (text && text.length > 100) {
            return text.substring(0, 97) + '...';
          }
          
          return text || null;
        }
      } catch (error) {
        return null;
      }
      return null;
    },

    async copyToClipboard(data) {
      try {
        const text = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
        await navigator.clipboard.writeText(text);
        this.showNotification('Copié dans le presse-papiers', 'success');
      } catch (err) {
        consoleError('Failed to copy:', err);
        this.showNotification('Erreur lors de la copie', 'error');
      }
    },

    showNotification(message, type = 'success') {
      if (this.notification.timeout) {
        clearTimeout(this.notification.timeout);
      }

      this.notification = {
        show: true,
        message,
        type,
        timeout: setTimeout(() => {
          this.notification.show = false;
        }, 3000)
      };
    }
  },

  async mounted() {
    if (!this.whatsappAccountId) {
      this.showNotification('ID de compte WhatsApp manquant', 'error');
      this.$router.push('/dashboard/whatsapp-signup');
      return;
    }

    await Promise.all([
      this.fetchAccountInfo(),
      this.fetchLogs(),
      this.fetchStats()
    ]);
  }
};
</script>

<style scoped>
.api-logs-view {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

/* Header styles removed in favor of PageHeader component */
.back-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: white;
  border: 1px solid #E8ECEF;
  border-radius: 8px;
  color: #6F767E;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #F9FAFB;
  border-color: #1EA355;
  color: #1EA355;
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #1EA355;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-btn:hover:not(:disabled) {
  background: #167F42;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(30, 163, 85, 0.2);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.stat-card {
  background: white;
  border: 1px solid #E8ECEF;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s ease;
}

.stat-card:hover {
  border-color: #1EA355;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon.success {
  background: #E6F7EE;
  color: #1EA355;
}

.stat-icon.error {
  background: #FEF2F2;
  color: #DC2626;
}

.stat-icon.total {
  background: #EFF6FF;
  color: #3B82F6;
}

.stat-icon.time {
  background: #FFF5E6;
  color: #F59E0B;
}

.stat-icon svg {
  width: 20px;
  height: 20px;
}

.stat-content {
  flex: 1;
  min-width: 0;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #1A1D1F;
  line-height: 1.2;
  margin-bottom: 2px;
}

.stat-label {
  font-size: 12px;
  color: #6F767E;
  font-weight: 500;
}

/* Filters */
.filters-section {
  background: white;
  border: 1px solid #E8ECEF;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 20px;
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 140px;
}

.filter-group label {
  font-size: 11px;
  font-weight: 600;
  color: #6F767E;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-group select,
.filter-group input {
  padding: 8px 12px;
  border: 1px solid #E8ECEF;
  border-radius: 8px;
  font-size: 13px;
  color: #1A1D1F;
  background: white;
  transition: all 0.2s ease;
}

.filter-group select:focus,
.filter-group input:focus {
  outline: none;
  border-color: #1EA355;
  box-shadow: 0 0 0 3px rgba(30, 163, 85, 0.1);
}

.reset-filters-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: white;
  border: 1px solid #E8ECEF;
  border-radius: 8px;
  color: #6F767E;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.reset-filters-btn:hover {
  background: #F9FAFB;
  color: #DC2626;
  border-color: #DC2626;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 80px 20px;
  background: #F9FAFB;
  border-radius: 12px;
}

.loading-spinner {
  color: #1EA355;
  margin: 0 auto 16px;
  display: flex;
  justify-content: center;
}

.loading-state p {
  color: #6F767E;
  font-size: 16px;
}

/* Logs Container */
.logs-container {
  background: transparent;
}

.logs-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

/* Log Card */
.log-card {
  background: white;
  border-radius: 10px;
  border-left: 4px solid transparent;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.log-card.outgoing {
  border-left-color: #3B82F6;
  background: linear-gradient(to right, #EFF6FF 0%, white 20px);
}

.log-card.incoming {
  border-left-color: #9333EA;
  background: linear-gradient(to right, #F3E8FF 0%, white 20px);
}

.log-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.log-card.outgoing:hover {
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.2);
}

.log-card.incoming:hover {
  box-shadow: 0 4px 16px rgba(147, 51, 234, 0.2);
}

.log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  cursor: pointer;
  user-select: none;
}

.log-main-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.log-direction-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.log-direction-icon.outgoing {
  background: #DBEAFE;
  color: #3B82F6;
}

.log-direction-icon.incoming {
  background: #EDE9FE;
  color: #9333EA;
}

.log-summary {
  flex: 1;
  min-width: 0;
}

.log-title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.5px;
}

.type-badge.outgoing {
  background: #3B82F6;
  color: white;
}

.type-badge.incoming {
  background: #9333EA;
  color: white;
}

.method-badge {
  padding: 3px 8px;
  background: #F3F4F6;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  font-family: monospace;
  color: #6F767E;
  text-transform: uppercase;
}

.status-badge {
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.status-badge.success {
  background: #E6F7EE;
  color: #1EA355;
}

.status-badge.error {
  background: #FEF2F2;
  color: #DC2626;
}

.status-badge.timeout {
  background: #FFF5E6;
  color: #F59E0B;
}

.status-badge.pending {
  background: #F3F4F6;
  color: #6F767E;
}

.retry-badge {
  padding: 3px 8px;
  background: #FFF5E6;
  color: #F59E0B;
  border-radius: 6px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.business-status-badge {
  padding: 4px 10px;
  background: #25D366;
  color: white;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.business-status-badge svg {
  width: 12px;
  height: 12px;
}

.log-essential-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.info-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: #F9FAFB;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #6F767E;
}

.info-chip svg {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}

.info-chip.success {
  background: #E6F7EE;
  color: #1EA355;
}

.info-chip.client-error {
  background: #FFF5E6;
  color: #F59E0B;
}

.info-chip.server-error {
  background: #FEF2F2;
  color: #DC2626;
}

.info-chip.time {
  color: #9CA3AF;
  font-size: 10px;
}

.info-chip.recipient {
  background: #D1FAE5;
  color: #047857;
  font-weight: 700;
  font-family: monospace;
}

.info-chip.phone {
  background: #EFF6FF;
  color: #3B82F6;
  font-weight: 700;
}

.info-chip.message-type {
  background: #F3E8FF;
  color: #9333EA;
  font-weight: 700;
  text-transform: capitalize;
}

.message-preview {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 8px;
  padding: 8px 10px;
  background: #F9FAFB;
  border-radius: 6px;
  border-left: 2px solid #E5E7EB;
  font-size: 12px;
  color: #6F767E;
  line-height: 1.4;
}

.message-preview svg {
  flex-shrink: 0;
  margin-top: 2px;
  color: #9CA3AF;
}

.message-preview span {
  flex: 1;
  word-break: break-word;
}

.expand-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: #F3F4F6;
  color: #6F767E;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.expand-btn:hover {
  background: #E5E7EB;
  color: #1A1D1F;
}

.expand-btn.expanded {
  background: #1EA355;
  color: white;
  transform: rotate(180deg);
}

.log-body {
  padding: 0 16px 14px 16px;
  border-top: 1px solid #F3F4F6;
}

.log-details-grid {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 12px;
  background: #F9FAFB;
  border-radius: 6px;
}

.detail-row.full {
  flex-direction: column;
  gap: 6px;
}

.detail-label {
  font-size: 10px;
  color: #9CA3AF;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 80px;
}

.detail-row.full .detail-label {
  min-width: auto;
}

.detail-value {
  font-size: 12px;
  color: #1A1D1F;
  font-weight: 600;
  flex: 1;
}

.detail-value.url {
  font-family: monospace;
  font-size: 11px;
  color: #3B82F6;
  word-break: break-all;
}

.whatsapp-message {
  background: #F0FFF4;
  border: 1px solid #D1FAE5;
}

.message-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #1EA355;
  margin-bottom: 8px;
}

.message-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 11px;
  color: #6F767E;
}

.message-details span {
  display: flex;
  align-items: center;
}

.message-details strong {
  color: #1A1D1F;
  margin-left: 4px;
}

.error-message {
  background: #FEF2F2;
  border: 1px solid #FCA5A5;
  color: #DC2626;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 11px;
  line-height: 1.5;
}

.error-message svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.log-actions {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #F3F4F6;
  display: flex;
  justify-content: flex-end;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn svg {
  width: 14px;
  height: 14px;
}

.action-btn.primary {
  background: #1EA355;
  color: white;
}

.action-btn.primary:hover {
  background: #167F42;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(30, 163, 85, 0.2);
}

/* Expand Transition */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 20px 0 0;
}

.page-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: white;
  border: 1px solid #E8ECEF;
  border-radius: 8px;
  color: #6F767E;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.page-btn:hover:not(:disabled) {
  background: #F9FAFB;
  border-color: #1EA355;
  color: #1EA355;
}

.page-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.page-btn svg {
  width: 14px;
  height: 14px;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-number {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  background: white;
  border: 1px solid #E8ECEF;
  border-radius: 6px;
  color: #6F767E;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-number:hover:not(:disabled):not(.active) {
  background: #F9FAFB;
  border-color: #1EA355;
  color: #1EA355;
}

.page-number.active {
  background: #1EA355;
  color: white;
  border-color: #1EA355;
  box-shadow: 0 2px 4px rgba(30, 163, 85, 0.2);
}

.page-number:disabled {
  cursor: default;
  background: transparent;
  border: none;
  color: #D1D5DB;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: #F9FAFB;
  border-radius: 12px;
}

.empty-state svg {
  color: #C5D0D9;
  margin-bottom: 20px;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 600;
  color: #1A1D1F;
  margin: 0 0 8px 0;
}

.empty-state p {
  color: #6F767E;
  font-size: 15px;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  backdrop-filter: blur(4px);
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.log-detail-modal {
  max-width: 1000px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #E8ECEF;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
}

.modal-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: #1A1D1F;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  padding: 8px;
  color: #6F767E;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: #E8ECEF;
  color: #1A1D1F;
}

.modal-body {
  padding: 24px;
}

/* Summary Cards */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
}

.summary-card {
  background: white;
  border: 1px solid #E8ECEF;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s ease;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.summary-card.outgoing {
  border-left: 3px solid #3B82F6;
}

.summary-card.incoming {
  border-left: 3px solid #9333EA;
}

.summary-card.status-success {
  border-left: 3px solid #1EA355;
}

.summary-card.status-error {
  border-left: 3px solid #DC2626;
}

.summary-card-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #F9FAFB;
  color: #6F767E;
}

.summary-card.outgoing .summary-card-icon {
  background: #EFF6FF;
  color: #3B82F6;
}

.summary-card.incoming .summary-card-icon {
  background: #F3E8FF;
  color: #9333EA;
}

.summary-card.status-success .summary-card-icon {
  background: #E6F7EE;
  color: #1EA355;
}

.summary-card.status-error .summary-card-icon {
  background: #FEF2F2;
  color: #DC2626;
}

.summary-card-content {
  flex: 1;
  min-width: 0;
}

.summary-card-label {
  font-size: 11px;
  color: #9CA3AF;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.summary-card-value {
  font-size: 16px;
  font-weight: 700;
  color: #1A1D1F;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Detail Section Modern */
.detail-section-modern {
  margin-bottom: 24px;
  background: #F9FAFB;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #E8ECEF;
}

.detail-section-modern:last-child {
  margin-bottom: 0;
}

.section-header-modern {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #E8ECEF;
}

.section-header-modern svg {
  color: #1EA355;
  flex-shrink: 0;
}

.section-header-modern h4 {
  font-size: 16px;
  font-weight: 700;
  color: #1A1D1F;
  margin: 0;
}

.section-header-modern.error-header svg {
  color: #DC2626;
}

/* Info List */
.info-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.info-list-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  background: white;
  border-radius: 6px;
  border-left: 3px solid #E8ECEF;
}

.info-list-label {
  font-size: 12px;
  font-weight: 700;
  color: #6F767E;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 120px;
  flex-shrink: 0;
}

.info-list-value {
  font-size: 13px;
  font-weight: 600;
  color: #1A1D1F;
  flex: 1;
  word-break: break-word;
}

.info-list-value.url-value {
  color: #3B82F6;
  font-family: monospace;
  font-size: 12px;
}

.info-list-value.monospace-value {
  font-family: monospace;
  font-size: 12px;
  color: #6F767E;
}

.status-code-badge-modern {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
}

.status-code-badge-modern.success {
  background: #E6F7EE;
  color: #1EA355;
}

.status-code-badge-modern.client-error {
  background: #FFF5E6;
  color: #F59E0B;
}

.status-code-badge-modern.server-error {
  background: #FEF2F2;
  color: #DC2626;
}

/* Code Section Modern */
.code-section-modern {
  margin-top: 16px;
  border: 1px solid #E8ECEF;
  border-radius: 8px;
  overflow: hidden;
  background: white;
}

.code-header-modern {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: #1A1D1F;
  color: white;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.copy-btn-modern {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #2D3748;
  border: 1px solid #4A5568;
  border-radius: 6px;
  color: white;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.copy-btn-modern:hover {
  background: #4A5568;
  border-color: #718096;
}

.code-block-modern {
  padding: 16px;
  background: #1A1D1F;
  color: #E8ECEF;
  font-size: 11px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Courier New', monospace;
  line-height: 1.6;
  overflow-x: auto;
  margin: 0;
  max-height: 300px;
}

/* WhatsApp Section */
.whatsapp-section {
  background: #F0FFF4;
  border-color: #D1FAE5;
}

.whatsapp-section .section-header-modern {
  border-color: #A7F3D0;
}

.whatsapp-section .section-header-modern svg {
  color: #1EA355;
}

.whatsapp-section .info-list-item {
  background: white;
  border-left-color: #1EA355;
}

/* Error Section Modern */
.error-section-modern {
  background: #FEF2F2;
  border-color: #FCA5A5;
}

.error-section-modern .section-header-modern {
  border-color: #FCA5A5;
}

.error-content-modern {
  background: white;
  border: 1px solid #FCA5A5;
  border-radius: 8px;
  padding: 16px;
}

.error-item-modern {
  color: #DC2626;
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 8px;
}

.error-item-modern:last-child {
  margin-bottom: 0;
}

.error-item-modern strong {
  font-weight: 700;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 4px;
}

/* Notification */
.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 4000;
  padding: 16px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 300px;
  max-width: 400px;
}

.notification.success {
  background-color: #EBF7EE;
  color: #1EA355;
  border: 1px solid #D1E7DD;
}

.notification.error {
  background-color: #FEF2F2;
  color: #DC2626;
  border: 1px solid #FCA5A5;
}

.notification-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.notification-content svg {
  flex-shrink: 0;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Responsive */
@media (max-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .log-info {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .api-logs-view {
    padding: 16px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .filters-row {
    flex-direction: column;
  }

  .filter-group {
    width: 100%;
    min-width: 100%;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .pagination {
    flex-wrap: wrap;
  }

  .header-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .content-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .header-title h1 {
    font-size: 20px;
  }

  .log-card:hover {
    transform: none;
  }
}
</style>

